import type { Editor } from '@tiptap/react'
import type { Node as PMNode } from '@tiptap/pm/model'
import { closeHistory } from '@tiptap/pm/history'
import { colourAt, type ColourMark } from './prose-colours'

/**
 * The prose format painter (AB#331): what "Copy style" captures off the caret or the selection, and
 * how it is pasted onto another range — the prose counterpart of slides/format-painter.ts.
 *
 * The prose CRDT is Markdown, so the look of a run is five marks: bold, italic, strike, the text
 * colour and the highlight. There is no underline, font, size nor alignment to copy, and a link,
 * inline code or the block type (heading, list, paragraph) is content, not a look — never copied.
 */
export interface ProseMarks {
  bold: boolean
  italic: boolean
  strike: boolean
  textColour: string | null
  highlight: string | null
}

const TOGGLES = ['bold', 'italic', 'strike'] as const
const COLOURS: readonly ColourMark[] = ['textColour', 'highlight']

/** A colour mark of the selection; several colours: the first letter's. */
function colourOf(e: Editor, mark: ColourMark): string | null {
  const at = colourAt(e, mark)
  if (at !== 'mixed') return at
  const { from, to } = e.state.selection
  let first: string | null = null
  let found = false
  e.state.doc.nodesBetween(from, to, (n) => {
    if (found) return false
    if (!n.isText) return true
    found = true
    const m = n.marks.find((x) => x.type.name === mark)
    first = m ? String(m.attrs.color) : null
    return false
  })
  return first
}

/** The marks at the caret (those it would type with) or of the selection (a toggle: on only if the
 *  whole selection has it, as the bar shows it). */
export function proseMarksOf(e: Editor): ProseMarks {
  return {
    bold: e.isActive('bold'),
    italic: e.isActive('italic'),
    strike: e.isActive('strike'),
    textColour: colourOf(e, 'textColour'),
    highlight: colourOf(e, 'highlight'),
  }
}

const WORD = /[\p{L}\p{N}_'’-]/u

/** The word around a position (a click on a word paints it), or null between words. */
export function wordAt(doc: PMNode, pos: number): { from: number; to: number } | null {
  const $p = doc.resolve(pos)
  if (!$p.parent.isTextblock) return null
  const start = $p.start()
  // One character per inline leaf (an image, a hard break), so the offsets are the positions'.
  const text = doc.textBetween(start, $p.end(), undefined, '￼')
  const at = pos - start
  let a = at
  let b = at
  while (a > 0 && WORD.test(text[a - 1]!)) a--
  while (b < text.length && WORD.test(text[b]!)) b++
  return a === b ? null : { from: start + a, to: start + b }
}

/**
 * Give a range (default: the selection) exactly these marks — the source's set, the others of the
 * five removed, so it ends up looking like the source. One command chain = one transaction, kept
 * out of the previous undo step: one paint, one Ctrl+Z. A given range leaves the selection where it
 * was. Returns whether anything was painted (an empty range, a read-only editor: no).
 */
export function paintProse(e: Editor, marks: ProseMarks, range?: { from: number; to: number }): boolean {
  if (e.isDestroyed || !e.isEditable) return false
  const sel = e.state.selection
  const { from, to } = range ?? sel
  if (from >= to) return false
  let c = e.chain().command(({ tr }) => {
    closeHistory(tr)
    return true
  })
  if (range) c = c.setTextSelection(range)
  for (const m of TOGGLES) c = marks[m] ? c.setMark(m) : c.unsetMark(m)
  for (const m of COLOURS) {
    const colour = marks[m]
    c = colour ? c.setMark(m, { color: colour }) : c.unsetMark(m)
  }
  if (range) c = c.setTextSelection({ from: sel.from, to: sel.to })
  return c.run()
}
